import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import * as api from "../../lib/api.ts";
import { unopenedHomeworkText } from "../../lib/plurals.ts";

/**
 * Извещение ученику о заданном: число над разделом «Домашка» и полоса на
 * расписании.
 *
 * **Зачем вообще.** До 20.08.2026 ученик не узнавал о домашке никак, пока сам
 * не заходил в раздел, и это было записано в проекте домашки честными словами
 * («как ученик узнаёт о домашке: никак, пока не зашёл»). Просьба преподавателя
 * 20.08.2026 дословно: «он раз в неделю на платформу заходит, зайдёт и такой,
 * о, домашку задали». Обещание проекта звучало как «заданное видно при любом
 * входе», и до сих пор оно выполнялось только для того, кто догадался открыть
 * нужный раздел.
 *
 * ⚠️ **Это не транспорт уведомлений и раздела 4 CLAUDE.md не касается.**
 * Наружу не уходит ничего: число показывается тому, кто уже открыл кабинет.
 * Запрет там на доставку критичного через чужие мессенджеры, а не на подпись в
 * собственном интерфейсе.
 *
 * **Два вида, и они отвечают на разные вопросы.**
 *
 *   ЧИСЛО У РАЗДЕЛА  висит на любом экране кабинета, отвечает «есть ли что-то»
 *   ПОЛОСА           стоит только на расписании, отвечает «что мне сейчас делать»
 *
 * Полоса живёт на расписании, а не над всеми экранами, как полоса часового
 * пояса, по двум причинам. Расписание это домашняя страница и то место, куда
 * приземляется ученик, погасивший ссылку приглашения (`Root` переадресует
 * туда), значит вход он не пропустит. А над самим разделом «Домашка» полоса
 * стояла бы поверх того самого списка, о котором говорит, и это тавтология.
 *
 * ⚠️ **Закрыть полосу нельзя, и кнопки «закрыть» у неё нет намеренно.**
 * Способ убрать её ровно один: открыть работу. Кнопка закрытия у извещения,
 * которое всё равно вернётся при следующей загрузке, это лишнее нажатие с
 * нулевым эффектом; а извещение, закрывающееся навсегда, не выполнило бы то,
 * ради чего заведено. Ученик, которому сейчас некогда, просто уходит на другой
 * экран, где остаётся число у раздела.
 */

/**
 * Сколько работ ученику задали и он их ещё не открывал.
 *
 * ⚠️ **Поводов перечесть два, и одного мало.** Число меняет ровно одно
 * событие, и происходит оно на соседнем экране: `GET /v1/homework/:id` роли
 * ученика ставит `first_opened_at`. Каркас кабинета при переходах не
 * размонтируется, значит без перечитывания значок горел бы до перезагрузки
 * вкладки.
 *
 * Смены адреса при этом НЕДОСТАТОЧНО, и это гонка, а не мелочь: перечитывание
 * по адресу уходит в ту же миллисекунду, что и запрос карточки, а порядок
 * обработки `UPDATE` и `count(*)` в базе ничем не задан. Поэтому второй повод
 * приходит от самой карточки, когда она уже приехала (`useHomeworkNewsRefresh`
 * ниже). Смена адреса при этом остаётся: она ловит всё остальное, от возврата
 * из комнаты урока до перехода между разделами.
 *
 * ⚠️ `enabled` это `me.learning === true`, то есть «у человека есть
 * преподаватели». Отсутствие поля (старый API) означает «не знаем» и запрос не
 * уходит вовсе: у преподавателя число всегда ноль, и спрашивать его на каждом
 * переходе незачем.
 */
export function useHomeworkNews(enabled: boolean): { count: number; refresh: () => void } {
  const [count, setCount] = useState(0);
  /**
   * Счётчик поводов перечитать. Число, а не булев флаг: два повода подряд
   * (смена адреса и следом ответ карточки) обязаны дать два перечитывания, а
   * переключаемый флаг второе из них проглотил бы.
   */
  const [beat, setBeat] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await api.getUnopenedHomework();
        if (alive) setCount(res.count);
      } catch {
        // Извещение это справка, а не работа человека: отказ гасит значок и
        // молчит. Красное «HTTP 500» в сайдбаре поверх любого экрана было бы
        // хуже отсутствующего числа
        if (alive) setCount(0);
      }
    })();
    return () => {
      alive = false;
    };
  }, [enabled, pathname, beat]);

  const refresh = useCallback(() => setBeat((n) => n + 1), []);
  return { count, refresh };
}

/**
 * Просьба перечесть счёт: её зовёт карточка работы, когда карточка приехала.
 *
 * ⚠️ **Без этого счёт врал бы ровно на том экране, ради которого заведён.**
 * Одной смены адреса мало, и это гонка, а не недосмотр: отметку «ученик открыл»
 * ставит сам запрос карточки (`GET /v1/homework/:id`), а перечитывание по
 * смене адреса уходит в ту же миллисекунду, что и он. Порядок, в котором база
 * обработает `UPDATE` карточки и `count(*)` счётчика, ничем не задан, то есть
 * примерно в половине случаев счёт считался бы ДО фиксации отметки. Значок
 * оставался бы гореть всё время, пока открыта работа, которую только что
 * открыли, и гас бы лишь при следующем переходе.
 *
 * Умолчание пустое: контекста нет у экранов вне каркаса кабинета (комната
 * урока, архив, погашение ссылки), и звать оттуда некого. Пустая функция, а не
 * отказ, потому что для карточки это не ошибка: значка на тех экранах нет вовсе.
 */
const RefreshNews = createContext<() => void>(() => {});

export const HomeworkNewsRefresh = RefreshNews.Provider;

export function useHomeworkNewsRefresh(): () => void {
  return useContext(RefreshNews);
}

/** Полоса «вам задали домашнее задание». Ноль означает, что полосы нет вовсе. */
export function HomeworkNewsBar(props: { count: number }) {
  if (props.count === 0) return null;

  return (
    <div className="newsbar" role="status">
      <span className="newsbar__text">Вам задали {unopenedHomeworkText(props.count)}.</span>
      {/*
        Акцентная ссылка, в отличие от кнопки в полосе часового пояса. Разница
        не в весе просьбы, а в том, что там вопрос («ваш пояс такой?»), а здесь
        новость с ровно одним продолжением. Конкуренции с главной кнопкой
        экрана под полосой не возникает: на расписании акцента нет вовсе, и это
        записано в шапке `SchedulePage`.
      */}
      <Link className="btn btn--primary" to="/homework">
        {props.count === 1 ? "Открыть" : "Смотреть"}
      </Link>
    </div>
  );
}
